
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

import { Activity } from './schema/activitySchema';
import { ActivityTypeIcon } from './ActivityTypeIcon';
import { ActivityStatus } from './ActivityStatus';
import { ActivityPriority } from './ActivityPriority';

interface ActivityCardProps {
  activity: Activity;
  className?: string;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity, className }) => {
  const navigate = useNavigate();
  
  // Format the scheduled date
  const formattedDate = format(new Date(activity.scheduled_for), 'EEEE d MMMM', { locale: fr });
  const formattedTime = format(new Date(activity.scheduled_for), 'HH:mm');
  
  // Get the activity type label
  const getActivityTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'irrigation': 'Irrigation',
      'fertilization': 'Fertilisation',
      'weather': 'Météo',
      'harvest': 'Récolte',
      'planting': 'Plantation',
      'maintenance': 'Maintenance',
      'inspection': 'Inspection',
      'other': 'Autre'
    };
    return typeMap[type] || 'Autre';
  };
  
  const handleClick = () => {
    navigate(`/activities/${activity.id}`);
  };
  
  // Determine if the activity is today
  const isToday = () => {
    const today = new Date();
    const activityDate = new Date(activity.scheduled_for);
    return (
      today.getDate() === activityDate.getDate() &&
      today.getMonth() === activityDate.getMonth() &&
      today.getFullYear() === activityDate.getFullYear()
    );
  };

  return (
    <Card 
      className={cn(
        "cursor-pointer hover:shadow-md transition-shadow duration-200",
        activity.status === 'completed' ? "opacity-80" : "",
        className
      )}
      onClick={handleClick}
    >
      <CardHeader className="pb-2 flex flex-row items-start justify-between">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <ActivityTypeIcon type={activity.activity_type} size={18} />
            <span className="text-sm font-medium text-gray-600">
              {getActivityTypeLabel(activity.activity_type)}
            </span>
          </div>
          <h3 className="font-semibold text-lg mt-1">{activity.title}</h3>
        </div>
        <ActivityStatus status={activity.status} size="sm" />
      </CardHeader>
      
      <CardContent className="pb-3">
        {activity.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-3">
            {activity.description}
          </p>
        )}
        
        <div className="flex items-center gap-1 text-sm">
          <span className={cn(
            "font-medium",
            isToday() ? "text-primary" : "text-gray-700 dark:text-gray-300"
          )}>
            {isToday() ? "Aujourd'hui" : formattedDate}
          </span>
          <span className="text-gray-400 mx-1">|</span>
          <Clock className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-gray-700 dark:text-gray-300">{formattedTime}</span>
        </div>
      </CardContent>
      
      <CardFooter className="pt-2 border-t flex justify-between">
        <ActivityPriority priority={activity.priority as 'low' | 'medium' | 'high'} />
        {activity.parcel_id && (
          <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">
            Parcelle associée
          </span>
        )}
      </CardFooter>
    </Card>
  );
};

export default ActivityCard;
